import { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import SoMSubNav from '../components/SoMSubNav';
import { clientsAPI, somPromptsAPI } from '../lib/api';
import { somPromptApiError } from '../lib/somPromptHelpers';
import { formatUsageMetric, somReconciliationLabel, somUsageEmpty } from '../lib/somUsageHelpers';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

function Metric({ label, value, testId }) {
  return (
    <div>
      <p className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase">{label}</p>
      <p className="text-[var(--text-primary)] font-mono text-lg" data-testid={testId}>{value}</p>
    </div>
  );
}

export default function SoMUsagePage() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    clientsAPI.list()
      .then((r) => {
        setClients(r.data || []);
        if (r.data?.length) setClientId(r.data[0].id);
      })
      .catch((err) => toast.error(somPromptApiError(err)));
  }, []);

  const loadUsage = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError('');
    try {
      const res = await somPromptsAPI.usage(clientId);
      setData(res.data || null);
    } catch (err) {
      const msg = somPromptApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { loadUsage(); }, [loadUsage]);

  const tavilyAvailable = data?.tavily?.available !== false && data?.tavily?.value_state !== 'unavailable';
  const reconLabel = somReconciliationLabel(data);
  const empty = somUsageEmpty(data);

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="som-usage-page">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-[var(--text-primary)] tracking-tight">
              SoM Usage
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-2xl">
              Current-month SoM accounting. Production and custom Run Now are separate. Tavily figures are a user-level compatibility mirror, not extra searches.
            </p>
          </div>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="w-full sm:w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" data-testid="som-usage-client-select">
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{c.display_name || c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <SoMSubNav />

        {error && (
          <div className="rounded-lg border border-[var(--error-border)] bg-[var(--error-bg)] px-4 py-3 text-sm text-[var(--error)]">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 border-2 border-[var(--accent-border)] border-t-[var(--accent)] rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <p className="text-xs font-mono text-[var(--text-secondary)]" data-testid="som-usage-month">
              Month {data?.current_month || 'Unavailable'}
            </p>

            {empty && (
              <p className="text-sm text-[var(--text-secondary)]" data-testid="som-usage-empty">
                No SoM usage recorded this month
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="nx-metric-card rounded-lg" data-testid="som-usage-production">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Production SoM</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Metric label="Executions" value={formatUsageMetric(data?.production?.executions)} testId="som-usage-production-executions" />
                  <Metric label="Usage credits" value={formatUsageMetric(data?.production?.credits, data?.production?.credits_available !== false)} testId="som-usage-production-credits" />
                </div>
              </div>
              <div className="nx-metric-card rounded-lg" data-testid="som-usage-custom">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Custom Run Now</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Metric label="Executions" value={formatUsageMetric(data?.custom_runs?.executions)} testId="som-usage-custom-executions" />
                  <Metric label="Usage credits" value={formatUsageMetric(data?.custom_runs?.credits, data?.custom_runs?.credits_available !== false)} testId="som-usage-custom-credits" />
                </div>
              </div>
              <div className="nx-metric-card rounded-lg" data-testid="som-usage-tavily">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Tavily</h3>
                <p className="text-[10px] text-[var(--text-tertiary)] font-mono mb-2">User-month compatibility · not client-scoped</p>
                <div className="grid grid-cols-2 gap-3">
                  <Metric label="Calls" value={formatUsageMetric(data?.tavily?.calls, tavilyAvailable)} testId="som-usage-tavily-calls" />
                  <Metric label="Credits" value={formatUsageMetric(data?.tavily?.credits, tavilyAvailable)} testId="som-usage-tavily-credits" />
                </div>
              </div>
              <div className="nx-metric-card rounded-lg" data-testid="som-usage-reconciliation">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">Reconciliation</h3>
                <p className="text-[var(--text-primary)] font-mono text-lg" data-testid="som-usage-reconciliation-status">{reconLabel}</p>
                <p className="text-[10px] text-[var(--text-tertiary)] font-mono mt-2">
                  Ledger {formatUsageMetric(data?.reconciliation?.production_ledger_entries)} · Tavily calls {formatUsageMetric(data?.reconciliation?.tavily_calls, tavilyAvailable)}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
